import React from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text'
import Input from 'core-system/Input'
import Dropdown from 'core-system/Dropdown'
import FlexContainer from 'core-system/FlexContainer'
import DatePicker from 'core-system/DatePicker'
import Checkbox from 'core-system/Checkbox'
import WorksiteGroupDropdown from 'features/Programs/Launch/Components/WorksiteGroupDropdown'
import launchProgramsWordings from 'features/Programs/Shared/wordings.json'
import { SegmentListType } from 'features/Programs/Shared/types'
import palette from 'core-system/Themes/palette'
import {
  LAST_DAY_OF_MONTH_LABEL,
  PAY_CADENCE_DAYS,
  PAY_FREQUENCY_TYPES,
} from '../constants'

const PageItem = styled.div`
  margin-bottom: 2rem;
`

const TitleContainer = styled(Text)`
  margin-bottom: 1rem;
  color: ${palette.text.primary};
`

const DatePickerPayCadence = ({
  placeholderText,
  selected,
  onSelect,
}: {
  placeholderText: string
  selected: Date | null
  onSelect: (date: Date) => void
}) => (
  <DatePicker
    placeholderText={placeholderText}
    selected={selected}
    onSelect={onSelect}
    dateFormat='d'
    openToDate={new Date(2024, 0, 1)}
  />
)

export interface PayCadenceFormValues {
  payCadenceName: string
  payCadenceFrequency: string | null
  payCadenceDay: string | null
  payCadenceDate: Date | null
  payCadenceDate2: Date | null
  selectedSegments: SegmentListType[]
  splitDeduction: boolean
  isLastDayOfMonth: boolean
}

interface PayCadenceFormProps {
  values: PayCadenceFormValues
  segmentItems: SegmentListType[]
  onChange: (values: Partial<PayCadenceFormValues>) => void
  error?: string | null
}

const PayCadenceForm = ({ values, segmentItems, onChange, error }: PayCadenceFormProps) => {
  const {
    payCadenceName,
    payCadenceFrequency,
    payCadenceDay,
    payCadenceDate,
    payCadenceDate2,
    selectedSegments,
    splitDeduction,
    isLastDayOfMonth,
  } = values

  const showLastDayOfMonth =
    payCadenceFrequency === 'monthly' || payCadenceFrequency === 'semi-monthly'

  return (
    <>
      <PageItem>
        <TitleContainer variant='h5'>
          {launchProgramsWordings.selectGroupTitle}
        </TitleContainer>
        <WorksiteGroupDropdown
          items={segmentItems}
          activeItems={selectedSegments}
          onItemCallback={(segments) => onChange({ selectedSegments: segments })}
          width='100%'
        />
      </PageItem>
      <Input
        placeholder='Enter Pay Cadence Name'
        value={payCadenceName}
        onChange={(e) => onChange({ payCadenceName: e.target.value })}
        height='3.5rem'
        marginBottom='1rem'
      />
      <Dropdown
        items={[...PAY_FREQUENCY_TYPES]}
        placeholder='Select Pay Frequency'
        active={payCadenceFrequency}
        itemCallback={({ id }) =>
          onChange({
            payCadenceFrequency: id,
            isLastDayOfMonth:
              id === 'monthly' || id === 'semi-monthly' ? isLastDayOfMonth : false,
          })
        }
        width='100%'
        marginBottom='1rem'
      />
      {(payCadenceFrequency === 'bi-weekly' || payCadenceFrequency === 'weekly') && (
        <Dropdown
          items={[...PAY_CADENCE_DAYS]}
          active={payCadenceDay}
          placeholder='Select Pay Day'
          width='100%'
          itemCallback={({ id }) => onChange({ payCadenceDay: id })}
          marginBottom='1rem'
        />
      )}
      {payCadenceFrequency === 'monthly' && (
        <>
          {!isLastDayOfMonth && (
            <DatePickerPayCadence
              placeholderText='Select Pay Date'
              selected={payCadenceDate}
              onSelect={(date) => onChange({ payCadenceDate: date })}
            />
          )}
          <Checkbox
            checked={isLastDayOfMonth}
            onClick={() => onChange({ isLastDayOfMonth: !isLastDayOfMonth })}
            label={LAST_DAY_OF_MONTH_LABEL}
            marginTop='1rem'
            marginBottom='1rem'
          />
        </>
      )}
      {payCadenceFrequency === 'semi-monthly' && (
        <>
          <FlexContainer
            flexDirection='row'
            alignItems='center'
            justifyContent='space-between'
          >
            <DatePickerPayCadence
              placeholderText={
                isLastDayOfMonth ? 'First Pay Date (day of month)' : 'First Pay Date'
              }
              selected={payCadenceDate}
              onSelect={(date) => onChange({ payCadenceDate: date })}
            />
            {!isLastDayOfMonth && (
              <DatePickerPayCadence
                placeholderText='Second Pay Date'
                selected={payCadenceDate2}
                onSelect={(date) => onChange({ payCadenceDate2: date })}
              />
            )}
          </FlexContainer>
          <Checkbox
            checked={isLastDayOfMonth}
            onClick={() => onChange({ isLastDayOfMonth: !isLastDayOfMonth })}
            label={LAST_DAY_OF_MONTH_LABEL}
            marginTop='1rem'
            marginBottom='1rem'
          />
        </>
      )}
      {payCadenceFrequency === 'bi-weekly' && (
        <DatePickerPayCadence
          placeholderText='Select Pay Date'
          selected={payCadenceDate}
          onSelect={(date) => onChange({ payCadenceDate: date })}
        />
      )}
      <Checkbox
        checked={splitDeduction}
        onClick={() => onChange({ splitDeduction: !splitDeduction })}
        label='Split deduction'
        marginTop='1rem'
        marginBottom='1rem'
      />
      {error && (
        <Text variant='body2' color='error' marginTop='1rem'>
          {error}
        </Text>
      )}
    </>
  )
}

if (process.env.NODE_ENV !== 'production') {
  PayCadenceForm.displayName = 'PayCadenceForm'
}

export default React.memo(PayCadenceForm)
